// @flow
import React, { useContext } from "react";
import { MultiSelectionContext } from "data/context";
import PropTypes from "prop-types";
import Icons from "@hs/icons";
import Breadcrumb from "components/Breadcrumb";
import Attributes from "components/Attributes";
import { type SearchResultRowDTO } from "data/types";
import { removeSpaces } from "data/integrationTestUtils";
import withStickyToggle from "containers/StickyToggle";
import StickyToggleStyle from "containers/StickyToggle/style";
import { isHandheld } from "data/bowser";
import styles from "./RowResult.module.css";
import { getDefaultsConfig } from "data/constants";

type Props = {
    /** row number */
    index: number,
    /* CSS classname to apply */
    className: string,
    /* CSS styles to apply */
    style?: StyleSheet | Object,
    /* search result row object */
    row: SearchResultRowDTO,
    /** callback when user clicks whole row */
    onClick: () => void,
    /** whether breadcrumb should be readonly */
    isBreadcrumbReadonly: boolean,
    /** whether more button is rendered */
    actionsDisabled: boolean,
    /** StickyToggleHOC div ref */
    divRef: { current: null | React.ElementRef<ElementType> },
    /** ContextMenuHandler onTouchStart callback */
    onTouchStart: () => void,
    /** ContextMenuHandler onTouchMove callback */
    onTouchMove: () => void,
    /** ContextMenuHandler onTouchEnd callback */
    onTouchEnd: () => void,
    /** ContextMenuHandler onMouseDown callback */
    onMouseDown: () => void,
    /** ContextMenuHandler onMouseUp callback */
    onMouseUp: () => void,
    /** ContextMenuHandler onContextMenu callback */
    onContextMenu: () => void,
};

/**
 * will render one folder search result
 */
export const FolderResult = ({
    index,
    className,
    style,
    row,
    onClick,
    isBreadcrumbReadonly,
    actionsDisabled,
    isHovered,
    divRef,
    onMouseEnter,
    onMouseLeave,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown,
    onMouseUp,
    onContextMenu,
}: Props) => {
    /* istanbul ignore next */
    const { multiple, renderCheckbox } = useContext(MultiSelectionContext) || {
        multiple: false,
        renderCheckbox: () => null,
    };

    const defaultsConfig = getDefaultsConfig(
        row.itemUri,
        row.breadcrumb.formatId
    );
    const scopeFromLevel = defaultsConfig?.scopeFromLevel ?? -1;

    return (
        <div
            onClick={onClick}
            className={className}
            style={style}
            ref={divRef}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onContextMenu={onContextMenu}
            data-test={`folderResult${removeSpaces(row.title)}`}
        >
            {(multiple || (isHovered && !isHandheld())) &&
                renderCheckbox(index, styles.checkbox)}
            <header className={styles.header}>
                <Icons.Custom
                    elementType={row.elementType}
                    icon={row.icon}
                    color={row.color}
                    className={styles.icon}
                />
                <span>{row.title}</span>
                <Breadcrumb
                    icon={row.breadcrumb.icon}
                    color={row.breadcrumb.color}
                    isReadonly={isBreadcrumbReadonly}
                    siteType={row.breadcrumb.siteType}
                    parts={row.breadcrumb.parts}
                    className={styles.bc}
                    leafActive={true}
                    scopeFromLevel={scopeFromLevel}
                />
            </header>
            <Attributes className={styles.footer} attributes={row.attributes} />
        </div>
    );
};
FolderResult.displayName = "FolderResult";
FolderResult.propTypes = {
    index: PropTypes.number.isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    row: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired,
    isBreadcrumbReadonly: PropTypes.bool,
};

const FolderResultWithStickyToggle = withStickyToggle({
    renderDiv: false,
    usePortal: false,
    getStyle: StickyToggleStyle.List,
})(FolderResult);

export default FolderResultWithStickyToggle;
